import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useRazorpay } from "../../lib/useRazorpay";
import { api } from "../../lib/api";

function PricingSkeleton() {
  return (
    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-2xl border border-slate-200 p-8 animate-pulse bg-white">
          <div className="h-5 bg-slate-200 rounded w-1/2 mb-4" />
          <div className="h-8 bg-slate-200 rounded w-2/3 mb-6" />
          <div className="space-y-3 mb-8">
            {[0, 1, 2, 3].map((j) => (
              <div key={j} className="h-4 bg-slate-100 rounded w-full" />
            ))}
          </div>
          <div className="h-11 bg-slate-200 rounded-xl w-full" />
        </div>
      ))}
    </div>
  );
}

export default function PricingPage() {
  const { user } = useAuth();
  const { openCheckout } = useRazorpay();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoTriggered, setAutoTriggered] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/plans");
      const data = res.data;
      setPlans(data.plans || []);
    } catch (e) {
      setError("Could not load plans. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Auto-trigger checkout when redirected from login/register with ?plan=
  useEffect(() => {
    if (!user || !plans.length || autoTriggered) return;
    const planId = searchParams.get("plan");
    if (!planId) return;
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      setAutoTriggered(true);
      openCheckout(plan);
    }
  }, [user, plans, searchParams, autoTriggered, openCheckout]);

  const handleSubscribe = (plan) => {
    if (!user) {
      navigate(`/register?plan=${plan.id}`);
    } else {
      openCheckout(plan);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="text-center py-16 px-4">
        <h1 className="text-4xl font-extrabold text-slate-900 mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-lg text-slate-600 max-w-xl mx-auto">
          Choose the plan that fits your team. Start with a 7-day free trial — no credit card required.
        </p>
      </div>

      {/* Plans */}
      <div className="pb-20 px-4">
        {loading && <PricingSkeleton />}

        {error && (
          <div className="text-center py-16">
            <p className="text-slate-600 mb-4">{error}</p>
            <button
              onClick={fetchPlans}
              className="px-6 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, idx) => {
              const isPopular = idx === 1;
              return (
                <div
                  key={plan.id}
                  className={`rounded-2xl border p-8 flex flex-col bg-white transition-shadow hover:shadow-lg ${
                    isPopular
                      ? "border-blue-500 ring-2 ring-blue-500 ring-offset-2 shadow-lg"
                      : "border-slate-200"
                  }`}
                >
                  {isPopular && (
                    <div className="mb-3">
                      <span className="bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h2>
                  {plan.description && (
                    <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                  )}
                  <div className="mb-6">
                    <span className="text-4xl font-extrabold text-slate-900">
                      {plan.currency === "INR" ? "₹" : plan.currency}{plan.price.toLocaleString()}
                    </span>
                    <span className="text-slate-500 ml-1 text-sm">
                      / {plan.duration_days} days
                    </span>
                  </div>
                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-2 mb-8 flex-1">
                      {plan.features.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="text-blue-500 mt-0.5 shrink-0">✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    onClick={() => handleSubscribe(plan)}
                    className={`mt-auto w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 ${
                      isPopular
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                  >
                    {user ? "Subscribe Now" : "Get Started"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && plans.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            No plans available right now. Please check back later.
          </div>
        )}
      </div>
    </div>
  );
}
